// 路由工具函数

import React from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

// 路由元数据类型
export interface RouteHandle {
  title?: string;
  description?: string;
  requiresAuth?: boolean;
  roles?: string[];
  preload?: () => Promise<any>;
}

// 导航选项
export interface NavigationOptions {
  replace?: boolean;
  state?: any;
  preventScrollReset?: boolean;
}

// 增强的导航Hook
export const useEnhancedNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateTo = useCallback((
    to: string, 
    options: NavigationOptions = {}
  ) => {
    const { replace = false, state, preventScrollReset = false } = options;
    
    navigate(to, {
      replace,
      state,
      preventScrollReset
    });
  }, [navigate]);

  const goBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [navigate]);

  const goForward = useCallback(() => {
    navigate(1);
  }, [navigate]);

  const refresh = useCallback(() => {
    navigate(location.pathname + location.search, { replace: true });
  }, [navigate, location]);

  return {
    navigateTo,
    goBack,
    goForward,
    refresh,
    currentPath: location.pathname,
    currentSearch: location.search,
    currentState: location.state
  };
};

// 查询参数Hook
export const useQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getParam = useCallback((key: string): string | null => {
    return searchParams.get(key);
  }, [searchParams]);

  const setParam = useCallback((key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(key, value);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const removeParam = useCallback((key: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(key);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearParams = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const paramsObject = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  return {
    getParam,
    setParam,
    removeParam,
    clearParams,
    params: paramsObject,
    searchParams
  };
};

// 路由匹配Hook
export const useRouteMatch = () => {
  const location = useLocation();
  const params = useParams();

  const isExactMatch = useCallback((path: string): boolean => {
    return location.pathname === path;
  }, [location.pathname]);

  const isPathMatch = useCallback((path: string): boolean => {
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const getActiveClass = useCallback((
    path: string, 
    exactMatch = false,
    activeClass = 'active',
    inactiveClass = ''
  ): string => {
    const isMatch = exactMatch ? isExactMatch(path) : isPathMatch(path);
    return isMatch ? activeClass : inactiveClass;
  }, [isExactMatch, isPathMatch]);

  return {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    state: location.state,
    params,
    isExactMatch,
    isPathMatch,
    getActiveClass
  };
};

// 面包屑Hook
export const useBreadcrumbs = () => {
  const location = useLocation();

  const breadcrumbs = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const crumbs: Array<{ path: string; label: string; isLast?: boolean }> = [
      { path: '/', label: '首页' }
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // 根据路径生成标签
      let label = segment;
      switch (segment) {
        case 'discover':
          label = '发现';
          break;
        case 'favorites':
          label = '收藏';
          break;
        case 'about':
          label = '关于';
          break;
        case 'cors-test':
          label = 'CORS测试';
          break;
        case 'library':
          label = '书库';
          break;
        case 'sources':
          label = '书源管理';
          break;
        case 'reader':
          label = '阅读器';
          break;
        default:
          // 首字母大写
          label = segment.charAt(0).toUpperCase() + segment.slice(1);
      }

      crumbs.push({
        path: currentPath,
        label,
        isLast: index === pathSegments.length - 1
      });
    });

    return crumbs;
  }, [location.pathname]);

  return breadcrumbs;
};

// 页面标题Hook
export const usePageTitle = (title?: string) => {
  const location = useLocation();

  useMemo(() => {
    if (title) {
      document.title = `${title} - Dopamine Web App`;
    } else {
      // 根据路径自动生成标题
      const pathTitle = getPageTitleFromPath(location.pathname);
      document.title = pathTitle ? `${pathTitle} - Dopamine Web App` : 'Dopamine Web App';
    }
  }, [title, location.pathname]);
};

// 根据路径获取页面标题
const getPageTitleFromPath = (pathname: string): string => {
  switch (pathname) {
    case '/':
      return '首页';
    case '/discover':
      return '发现';
    case '/favorites':
      return '收藏';
    case '/about':
      return '关于';
    case '/cors-test':
      return 'CORS测试';
    case '/library':
      return '书库';
    case '/sources':
      return '书源管理';
    case '/reader':
      return '阅读器';
    default:
      return '';
  }
};

// 路由预加载工具
export const preloadRoute = async (routePath: string) => {
  try {
    switch (routePath) {
      case '/':
        await import('@/pages/HomePage');
        break;
      case '/discover':
        await import('@/pages/DiscoverPage');
        break;
      case '/favorites':
        await import('@/pages/FavoritesPage');
        break;
      case '/about':
        await import('@/pages/AboutPage');
        break;
      case '/cors-test':
        await import('@/pages/CorsTestPage');
        break;
      case '/library':
        await import('@/pages/reading/BookLibraryPage');
        break;
      case '/sources':
        await import('@/pages/reading/BookSourcesPage');
        break;
      case '/reader':
        await import('@/pages/reading/BookReaderPage');
        break;
    }
  } catch (error) {
    console.warn(`Failed to preload route ${routePath}:`, error);
  }
};

// 路由守卫工具
export const createRouteGuard = (
  condition: () => boolean,
  redirectTo: string = '/'
) => {
  return (Component: React.ComponentType<any>) => {
    return (props: any) => {
      const navigate = useEnhancedNavigation();
      
      if (!condition()) {
        navigate.navigateTo(redirectTo, { replace: true });
        return null;
      }
      
      return React.createElement(Component, props);
    };
  };
};

// 常用路由路径常量
export const ROUTES = {
  HOME: '/',
  DISCOVER: '/discover',
  FAVORITES: '/favorites',
  ABOUT: '/about',
  CORS_TEST: '/cors-test',
  LIBRARY: '/library',
  SOURCES: '/sources',
  READER: '/reader'
} as const;

export type RouteKey = keyof typeof ROUTES;
export type RoutePath = typeof ROUTES[RouteKey];